import { AxiosResponse } from "axios";
import apiClient from "../../../../shared/infrastructure/api/client";
import {
    Payment,
    PaymentAccount,
    PaymentCreateInput,
    PaymentFilters,
    PaymentListPage,
    PaymentSendInput,
    PaymentUpdateInput,
    PayableSessionsPage,
} from "../../domain/types";

function toParams(filters: PaymentFilters): Record<string, string | number> {
    const params: Record<string, string | number> = {};
    if (filters.status) params.status = filters.status;
    if (filters.patientId) params.patient_id = filters.patientId;
    if (filters.sessionId) params.session_id = filters.sessionId;
    if (filters.fromDate) params.from_date = filters.fromDate;
    if (filters.toDate) params.to_date = filters.toDate;
    if (filters.offset !== undefined) params.offset = filters.offset;
    if (filters.limit !== undefined) params.limit = filters.limit;
    return params;
}

/**
 * There is deliberately no `downloadPdf` here. Alchely renders no document —
 * see `src/modules/payments/domain/types.ts`.
 */
export const PaymentAPI = {
    list: (filters: PaymentFilters = {}): Promise<AxiosResponse<PaymentListPage>> =>
        apiClient.get("/payments", { params: toParams(filters) }),

    /** Who a payment for this patient is charged through, and its defaults. */
    account: (patientId: string): Promise<AxiosResponse<PaymentAccount>> =>
        apiClient.get("/payments/account", { params: { patient_id: patientId } }),

    detail: (id: string): Promise<AxiosResponse<Payment>> =>
        apiClient.get(`/payments/${id}`),

    create: (payload: PaymentCreateInput): Promise<AxiosResponse<Payment>> =>
        apiClient.post("/payments", payload),

    update: (
        id: string,
        payload: PaymentUpdateInput
    ): Promise<AxiosResponse<Payment>> => apiClient.patch(`/payments/${id}`, payload),

    send: (
        id: string,
        payload: PaymentSendInput
    ): Promise<AxiosResponse<Payment>> =>
        apiClient.post(`/payments/${id}/send`, payload),

    markPaid: (id: string): Promise<AxiosResponse<Payment>> =>
        apiClient.post(`/payments/${id}/mark-paid`),

    cancel: (id: string): Promise<AxiosResponse<Payment>> =>
        apiClient.post(`/payments/${id}/cancel`),

    remind: (id: string): Promise<AxiosResponse<Payment>> =>
        apiClient.post(`/payments/${id}/remind`),

    /** One patient's sessions, to pick the one a payment is for. */
    sessionsOf: (
        patientId: string,
        params: { limit?: number } = {}
    ): Promise<AxiosResponse<PayableSessionsPage>> =>
        apiClient.get(`/sessions/patient/${patientId}`, { params }),
};
